import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import Search from 'uiKit/Search/Search'
import Select from 'react-select'
import html2canvas from 'html2canvas'
import {
  Section,
  LeftColumn,
  RightColumn,
  Divider,
  FiltersRow,
  TypeSelectWrap,
  StatusSelectWrap,
  SentimentSelectWrap,
  Spacer,
  ViewToggle,
  ToggleButton,
  PaginationSpacer,
} from './SupervisionStyle'
import SupervisionPie, { PieSlice } from './SupervisionPie'
import SupervisionBar from './SupervisionBar'
import SupervisionTable from './SupervisionTable'
import BreadCrumbs from 'uiKit/BreadCrumbs/BreadCrumbs'
import Headline from 'uiKit/texts/Headline'
import DateControl from 'uiKit/DateControlNew'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'
import * as S from '../../Dashboard.style'
import {
  loadSupervisionOptions,
  loadSupervisionTable,
  loadSupervisionChart,
  exportSupervisionTableCsv,
} from '../../api/dashboard'
import { DateControlContext } from 'contexts/DateControlContext'
import { getDashboardDateApiParams, shouldShowAccountStatusForDateRange } from '../../helpers/dashboardDateParams'
import { useScrollToTop } from 'hooks/useScrollToTop'
import { canViewCarecoDashboard } from '../../../../security'
import { isQcsBotPayload } from '../../../../constants/qcs'
import { InactiveTagOption } from '../InactiveTagSelectOption'

type Props = RouteComponentProps & { activeBot?: any, adminUser?: any }

export const Supervision: React.FC<Props> = ({ history, match, activeBot, adminUser }) => {
  useScrollToTop()
  const menuOpenTimeRef = useRef(0)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [status, setStatus] = useState<{ label: string; value: string } | null>(null)
  const [sentiment, setSentiment] = useState<{ label: string; value: string } | null>(null)
  const [type, setType] = useState<{ label: string; value: string; isInactive?: boolean } | null>(null)
  const [view, setView] = useState<'chart' | 'table'>('table')
  const { botId: routeBotId } = (match as any)?.params || {}
  const botId = routeBotId || activeBot?.id
  const isCarecoAdmin = useMemo(
    () => canViewCarecoDashboard(botId, adminUser?.botAccesses),
    [botId, adminUser],
  )
  const hideFrequency = isQcsBotPayload(activeBot)
  const [rows, setRows] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [chartLoading, setChartLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [pieData, setPieData] = useState<PieSlice[]>([])
  const [barData, setBarData] = useState({
    completedPositive: 0,
    completedNegative: 0,
    upcoming: 0,
    due: 0,
    inProgress: 0,
    overdue: 0,
    submitted: 0,
    submittedPositive: 0,
    submittedNegative: 0,
  })
  const {
    dateRange: { startDate, endDate },
    frequency,
  } = useContext(DateControlContext)
  const showAccountStatus = shouldShowAccountStatusForDateRange(isCarecoAdmin, frequency)
  const prevDatesRef = useRef({ start: startDate?.valueOf?.(), end: endDate?.valueOf?.() })
  const lastFetchKeyRef = useRef<string | null>(null)

  const handleDashboardClick = () => {
    const currentUrl = (match as any)?.url || ''
    const dashboardBase = currentUrl.replace(/\/supervision$/, '') || '/dashboard'
    history.push(dashboardBase)
  }

  const statusOptions = [
    { label: 'Status', value: '' },
    { label: 'Completed', value: 'completed' },
    { label: 'In progress', value: 'in_progress' },
    { label: 'To be completed', value: 'due' },
    { label: 'Overdue', value: 'overdue' },
    { label: 'Submitted', value: 'submitted' },
    { label: 'Upcoming', value: 'upcoming' },
  ]

  const sentimentOptions = [
    { label: 'All sentiments', value: '' },
    { label: 'Positive', value: 'POSITIVE' },
    { label: 'Negative', value: 'NEGATIVE' },
  ]

  const [typeOptions, setTypeOptions] = useState<{ label: string; value: string; isInactive?: boolean }[]>([
    { label: 'All supervisions', value: '', isInactive: false },
  ])
  const [typeOptionsLoading, setTypeOptionsLoading] = useState(false)

  // Reset options when the bot changes; will re-fetch on each menu open
  useEffect(() => {
    setTypeOptions([{ label: 'All supervisions', value: '', isInactive: false }])
    setPage(0)
    lastFetchKeyRef.current = null
  }, [botId])

  const handleTypeMenuOpen = async () => {
    menuOpenTimeRef.current = Date.now()
    if (!botId) return
    if (typeOptionsLoading) return
    try {
      setTypeOptionsLoading(true)
      const res: any[] = await loadSupervisionOptions(botId)
      if (Array.isArray(res)) {
        const opts = [
          { label: 'All supervisions', value: '', isInactive: false },
          ...res.map(o => ({ label: o.name, value: String(o.id), isInactive: o.status === 'INACTIVE' })),
        ]
        setTypeOptions(opts)
        // Reset guard timer after options load to cover the re-render scroll
        menuOpenTimeRef.current = Date.now()
      }
    } finally {
      setTypeOptionsLoading(false)
      // Reset guard timer after loading state changes to cover final re-render
      menuOpenTimeRef.current = Date.now()
    }
  }

  const shouldCloseOnScroll = (event: any) => {
    // Ignore scroll events within 300ms of menu open (caused by layout reflow)
    if (Date.now() - menuOpenTimeRef.current < 300) return false
    const target = (event && (event.target as HTMLElement)) || null
    if (!target) return true
    const withinMenu =
      target.closest('.supervision-select__menu')
      || target.closest('.supervision-select__menu-list')
    return !withinMenu
  }

  const buildSupervisionTableParams = (pageOverride = page) => {
    const params: any = { page: pageOverride + 1, size: 10 }
    if (type?.value) params.templateId = type.value
    if (status?.value) params.type = status.value
    if (sentiment?.value) params.sentiment = sentiment.value
    if (debouncedQuery) params.search = debouncedQuery
    return params
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query)
      setPage(0)
    }, 500)
    return () => clearTimeout(handle)
  }, [query])

  useEffect(() => {
    if (view !== 'table') {
      // Fetch fresh table data when returning from chart view.
      lastFetchKeyRef.current = null
      return
    }
    if (!botId) return

    const start = startDate?.valueOf?.()
    const end = endDate?.valueOf?.()
    const datesChanged = prevDatesRef.current.start !== start || prevDatesRef.current.end !== end
    const effectivePage = datesChanged ? 0 : page

    if (datesChanged) {
      prevDatesRef.current = { start, end }
      if (page !== 0) {
        setPage(0)
      }
    }

    const fetchKey = [
      botId,
      effectivePage,
      frequency,
      start,
      end,
      debouncedQuery,
      type?.value || '',
      status?.value || '',
      sentiment?.value || '',
      view,
    ].join('|')

    if (lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    const snapshot = {
      botId,
      page: effectivePage,
      start,
      end,
      query: debouncedQuery,
      templateId: type?.value || '',
      status: status?.value || '',
      sentiment: sentiment?.value || '',
    }
    const prev = (loadSupervisionTable as any)._prevSnap
    const isFirstLoad = !prev
    const pageOrDateChanged =
      isFirstLoad ||
      prev.botId !== snapshot.botId ||
      prev.page !== snapshot.page ||
      prev.start !== snapshot.start ||
      prev.end !== snapshot.end
    if (pageOrDateChanged) setLoading(true)

    const dateParams = getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin)
    loadSupervisionTable(botId, {
      ...dateParams,
      ...buildSupervisionTableParams(effectivePage),
    })
      .then((res: any) => {
        if (res && Array.isArray(res.content)) {
          const mapped = res.content.map((r: any, idx: number) => ({
            id: idx + 1,
            firstName: r.firstName,
            lastName: r.lastName,
            status: (r.type || '').toLowerCase().replace(/_/g, ' ') || '-',
            templateName: r?.templateName || '-',
            submttedAt: typeof r?.submittedAt === 'string' ? r.submittedAt.split(/\s|T/)[0] || '-' : '-',
            dueDate: typeof r?.dueDate === 'string' ? r.dueDate.split(/\s|T/)[0] || '-' : '-',
            sentiment: r.sentiment?.toLowerCase() || '-',
            accountStatus: r.userStatus || r.accountStatus || r.userAccountStatus || '',
          }))
          setRows(mapped)
          setTotalCount(Number(res.totalElements) || mapped.length)
        } else {
          setRows([])
          setTotalCount(0)
        }
      })
      .catch(() => {
        setRows([])
        setTotalCount(0)
      })
      .finally(() => {
        (loadSupervisionTable as any)._prevSnap = snapshot
        setLoading(false)
      })
  }, [botId, page, startDate, endDate, frequency, debouncedQuery, type, status, sentiment, view])

  useEffect(() => {
    if (!botId || view !== 'chart') return
    const snapshot = {
      botId,
      start: startDate?.valueOf?.(),
      end: endDate?.valueOf?.(),
      templateId: type?.value || '',
      status: status?.value || '',
    }
    const prev = (loadSupervisionChart as any)._prevSnap
    const isFirstLoad = !prev
    const dateOrFilterChanged =
      isFirstLoad ||
      prev.botId !== snapshot.botId ||
      prev.start !== snapshot.start ||
      prev.end !== snapshot.end ||
      prev.templateId !== snapshot.templateId ||
      prev.status !== snapshot.status
    if (dateOrFilterChanged) setChartLoading(true)

    const dateParams = getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin)
    const params: any = { ...dateParams }
    if (type?.value) params.templateId = type.value

    loadSupervisionChart(botId, params)
      .then((res: any) => {
        const rawPie: any[] = Array.isArray(res) ? res : res?.pieChart || []
        const mappedPie: PieSlice[] = rawPie.map(it => {
          const label = String(it?.label === 'Due' ? 'To be completed' : it?.label || '')
          const count = Number(it?.value) || 0
          const colorEntry = pieChartColors.find(c => c.label.toLowerCase() === label.toLowerCase())
          // Store raw count as value; SupervisionPie computes percentages from the real total
          return { label, value: count, color: (colorEntry && colorEntry.value) || pieChartColors[0].value, count }
        })
        setPieData(mappedPie)

        const sentiments: any = (res && (res.barChart || {})) || {}
        setBarData({
          completedPositive: Number(sentiments.completedPositive) || 0,
          completedNegative: Number(sentiments.completedNegative) || 0,
          upcoming: Number(sentiments.upcoming) || 0,
          due: Number(sentiments.due) || 0,
          inProgress: Number(sentiments.inProgress || sentiments.inprogress) || 0,
          overdue: Number(sentiments.overdue) || 0,
          submitted: Number(sentiments.submitted) || 0,
          submittedPositive: Number(sentiments.submittedPositive) || 0,
          submittedNegative: Number(sentiments.submittedNegative) || 0,
        })
      })
      .catch(() => {
        setPieData([])
        setBarData({
          completedPositive: 0,
          completedNegative: 0,
          upcoming: 0,
          due: 0,
          inProgress: 0,
          overdue: 0,
          submitted: 0,
          submittedNegative: 0,
          submittedPositive: 0,
        })
      })
      .finally(() => {
        (loadSupervisionChart as any)._prevSnap = snapshot
        setChartLoading(false)
      })
  }, [botId, view, startDate, endDate, frequency, type, status])

  const selectStyles = {
    control: base => ({
      ...base,
      minHeight: 45,
      height: 45,
      borderRadius: 10,
      borderColor: 'var(--color-input-stroke)',
      boxShadow: 'none',
      '&:hover': { borderColor: 'var(--color-input-stroke)' },
    }),
    valueContainer: base => ({ ...base, height: 45, padding: '0 12px' }),
    indicatorsContainer: base => ({ ...base, height: 45 }),
    menu: base => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'hidden',
      backgroundColor: state.isSelected ? '#E8F4FC' : base.backgroundColor,
      color: state.isSelected ? '#111827' : base.color,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#E8F4FC',
      },
      '&:hover': {
        backgroundColor: state.isSelected ? '#E8F4FC' : '#F3F4F6',
      },
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: 'none' }),
  }

  const searchInputStyle = {
    borderRadius: 8,
    width: '100%',
    maxWidth: '327px',
    height: '45px',
    padding: '14px 44px 19px 24px',
    minWidth: 200,
    flex: '1 1 240px',
    border: '1px solid rgba(226, 232, 234, 1)',
  }

  const pieChartColors = [
    { label: 'Upcoming', value: 'rgba(125, 168, 255, 1)' },
    { label: 'To be completed', value: 'rgba(255, 229, 155, 1)' },
    { label: 'In progress', value: 'rgba(155, 237, 255, 1)' },
    { label: 'Overdue', value: 'rgba(255, 155, 167, 1)' },
    { label: 'Submitted', value: 'rgba(188, 168, 243, 1)' },
    { label: 'Completed', value: 'rgba(142, 255, 176, 1)' },
  ]

  const handleExport = async () => {
    if (view === 'table') {
      onExportTable()
    } else {
      onExportChart()
    }
  }

  const onExportChart = async () => {
    if (!botId) return
    try {
      setIsExporting(true)
      const node = document.getElementById('supervision-chart')
      if (!node) return

      // ensure charts are fully visible during capture
      const canvas = await html2canvas(node, {
        backgroundColor: '#ffffff',
        scale: window.devicePixelRatio > 1 ? 2 : 1,
        useCORS: true,
        logging: false,
        onclone: clonedDoc => {
          const el = clonedDoc.getElementById('supervision-chart')
          if (el) {
            el.style.background = '#ffffff'
          }
        },
      })

      const dataUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = 'supervision-chart.png'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (e) {
    } finally {
      setIsExporting(false)
    }
  }

  const onExportTable = async () => {
    if (!botId) return
    try {
      setIsExporting(true)
      const dateParams = getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin)
      const blob = await exportSupervisionTableCsv(botId, {
        ...dateParams,
        ...buildSupervisionTableParams(),
      })

      if (!blob) return
      const url = window.URL.createObjectURL(blob as any)
      const a = document.createElement('a')
      a.href = url
      a.download = 'supervision-table.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      // Error feedback handled in API
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <S.Container>
      {isExporting && <LoaderOverlay />}
      <PaginationSpacer mt={30}>
        <BreadCrumbs items={[{ label: 'Dashboard', onClick: handleDashboardClick }, { label: 'Supervision' }]} />
      </PaginationSpacer>
      <S.HeadlineWrap>
        <Headline title="Supervision" />
        <DateControl
          isCarecoAdmin={hideFrequency || isCarecoAdmin}
          handleExport={hideFrequency || isCarecoAdmin ? handleExport : undefined}
          hideFrequency={hideFrequency}
        />
      </S.HeadlineWrap>
      <FiltersRow>
        {view === 'table' && (
          <Search
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name"
            inputStyle={searchInputStyle}
          />
        )}
        <TypeSelectWrap>
          <Select
            styles={selectStyles}
            options={typeOptions}
            value={type || typeOptions[0]}
            onChange={opt => {
              setType(opt as any)
              setPage(0)
            }}
            components={{ Option: InactiveTagOption }}
            onMenuOpen={handleTypeMenuOpen}
            isLoading={typeOptionsLoading}
            isSearchable={false}
            closeMenuOnScroll={shouldCloseOnScroll}
            classNamePrefix="supervision-select"
            menuPosition={'absolute'}
          />
        </TypeSelectWrap>
        {view === 'table' && (
          <StatusSelectWrap>
            <Select
              styles={selectStyles}
              options={statusOptions}
              value={status || statusOptions[0]}
              onChange={opt => {
                setStatus(opt as any)
                setPage(0)
              }}
              isSearchable={false}
              closeMenuOnScroll={shouldCloseOnScroll}
              classNamePrefix="supervision-select"
              menuPosition={'absolute'}
            />
          </StatusSelectWrap>
        )}
        {view === 'table' && (
          <SentimentSelectWrap>
            <Select
              styles={selectStyles}
              options={sentimentOptions}
              value={sentiment || sentimentOptions[0]}
              onChange={opt => {
                setSentiment(opt as any)
                setPage(0)
              }}
              isSearchable={false}
              closeMenuOnScroll={shouldCloseOnScroll}
              classNamePrefix="supervision-select"
              menuPosition={'absolute'}
            />
          </SentimentSelectWrap>
        )}
        <Spacer />
        <ViewToggle>
          <ToggleButton active={view === 'chart'} onClick={() => { setView('chart'); setPage(0) }}>
            Chart view
          </ToggleButton>
          <ToggleButton active={view === 'table'} onClick={() => { setView('table'); setPage(0) }}>
            Table view
          </ToggleButton>
        </ViewToggle>
      </FiltersRow>
      {view === 'chart' && (
        <div id="supervision-chart">
          {chartLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
              <LoaderScreen />
            </div>
          ) : (
            <Section>
              <LeftColumn>
                <SupervisionPie data={pieData} />
              </LeftColumn>
              <Divider />
              <RightColumn>
                <SupervisionBar title="Supervision sentiments" data={barData} />
              </RightColumn>
            </Section>
          )}
        </div>
      )}
      {view === 'table' && (
        <div>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
              <LoaderScreen />
            </div>
          ) : (
            <SupervisionTable
              rows={rows}
              totalCount={totalCount}
              page={page}
              onPageChange={setPage}
              loading={loading}
              showAccountStatus={showAccountStatus}
            />
          )}
        </div>
      )}
    </S.Container>
  )
}

const mapStateToProps = (state: any) => ({
  activeBot: state.activeBot,
  adminUser: state.adminUser,
})

export default withRouter(connect(mapStateToProps)(Supervision))
